import { createEffect, createSignal, For, Show } from 'solid-js'

import Footer from '/web/shared/aifans/Footer'
import { AccountLayout } from '/web/shared/aifans/AccountLayout'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { SubscriptionEvent, TokenTransaction } from '/common/types/aifans-schema'
import { useNavigate, useParams, A } from '@solidjs/router'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'

type TableData = SubscriptionEvent | TokenTransaction

export default function AdminTransactions() {
  const [tableData, setTableData] = createSignal<TableData[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const params = useParams()
  const navigate = useNavigate()
  const pageSize = 10

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    const res = await aifansSubscriptionApi.getAdminTransactions(page, pageSize)
    if (res.result) {
      setTableData(res.result.transactions)
      setTotalPages(Math.ceil(res.result.totalCount / pageSize))
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/admin/transactions' : `/admin/transactions/${page}`)
  }

  const formatDate = (dateString: string) => {
    return {
      date: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateString)),
      time: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(new Date(dateString)),
    }
  }

  const tokenSubtextFor = (item: TableData) => {
    if ('tokenAmount' in item) {
      const tokensToShow = item.tokensAdded !== undefined ? item.tokensAdded : item.tokenAmount
      if (tokensToShow !== undefined && tokensToShow > 0) {
        return `+${tokensToShow} tokens`
      }
    } else if ('tokensAdded' in item && item.tokensAdded !== undefined && item.tokensAdded > 0) {
      return `+${item.tokensAdded} tokens`
    }
    return ''
  }

  const thClass =
    'py-3.5 px-5 text-left font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.5)] lg:text-[13px] lg:py-[18px]'
  const tdClass =
    'py-4 px-5 align-top font-rajdhani text-[14px] text-[rgba(255,255,255,0.78)] lg:text-[16px] lg:py-5'

  const labelClass =
    'mb-0.5 font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]'
  const valueClass = 'font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]'

  const renderPagination = () => {
    const current = Number(params.page || '1')
    const total = totalPages()
    const delta = 2 // Number of pages to show before and after current page
    const range: (number | string)[] = []

    // Always show first page
    range.push(1)

    // Calculate start and end of range around current page
    let start = Math.max(2, current - delta)
    let end = Math.min(total - 1, current + delta)

    // Adjust if current page is near the beginning
    if (current - delta <= 2) {
      end = Math.min(total - 1, 1 + 2 * delta)
    }
    // Adjust if current page is near the end
    if (current + delta >= total - 1) {
      start = Math.max(2, total - 2 * delta)
    }

    // Add ellipsis after first page
    if (start > 2) {
      range.push('...')
    }

    // Add range around current page
    for (let i = start; i <= end; i++) {
      range.push(i)
    }

    // Add ellipsis before last page
    if (end < total - 1) {
      range.push('...')
    }

    // Always show last page if there is more than one page
    if (total > 1) {
      range.push(total)
    }

    const numberBase = 'rounded-lg px-3 py-1.5 font-rajdhani text-xs font-bold transition-colors'
    const inactive =
      'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] hover:bg-[rgba(255,255,255,0.1)]'

    return (
      <div class="flex items-center justify-center gap-2 border-t border-[rgba(255,255,255,0.06)] p-4">
        {/* Previous button */}
        <button
          onClick={() => current > 1 && changePage(current - 1)}
          disabled={current === 1}
          class={`${numberBase} ${inactive} ${
            current === 1 ? 'cursor-not-allowed opacity-40' : ''
          }`}
        >
          ←
        </button>

        {/* Page numbers */}
        <For each={range}>
          {(page) =>
            typeof page === 'string' ? (
              <span class="px-2 py-1.5 font-rajdhani text-xs text-[rgba(255,255,255,0.35)]">
                ...
              </span>
            ) : (
              <button
                onClick={() => changePage(page)}
                class={`${numberBase} ${current === page ? 'bg-cf-primary text-white' : inactive}`}
              >
                {page}
              </button>
            )
          }
        </For>

        {/* Next button */}
        <button
          onClick={() => current < total && changePage(current + 1)}
          disabled={current === total}
          class={`${numberBase} ${inactive} ${
            current === total ? 'cursor-not-allowed opacity-40' : ''
          }`}
        >
          →
        </button>
      </div>
    )
  }

  const typeSubtext = (item: TableData) => (
    <>
      {'reasonDescription' in item && item.type === 'cancellation' && item.reasonDescription && (
        <div class="font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">
          {item.reasonDescription}
        </div>
      )}
      {item.type === 'initial_payment' && (
        <div class="font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">Initial Payment</div>
      )}
      {item.type === 'recurring_payment' && (
        <div class="font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">Recurring</div>
      )}
    </>
  )

  return (
    <>
      <AccountLayout>
        <div class="mb-4 mt-3 font-bebas text-[28px] leading-none tracking-[1.5px] text-white sm:mb-5 sm:mt-0 sm:text-[38px] lg:text-[48px]">
          All Transactions
        </div>

        <div
          class="overflow-hidden rounded-2xl border border-[rgba(138,43,226,0.15)]"
          style={{ background: 'rgba(16,12,30,0.6)' }}
        >
          <Show
            when={tableData().length > 0}
            fallback={
              <div class="py-16 text-center">
                <p class="font-bebas text-xl text-[rgba(255,255,255,0.4)]">
                  No transactions available.
                </p>
              </div>
            }
          >
            {/* Desktop table */}
            <div class="hidden overflow-x-auto sm:block">
              <table class="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      background: 'rgba(138,43,226,0.08)',
                      'border-bottom': '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <th class={thClass}>Date</th>
                    <th class={thClass}>User</th>
                    <th class={thClass}>Type</th>
                    <th class={thClass}>Character</th>
                    <th class={thClass}>Transaction ID</th>
                    <th class={thClass}>Member ID</th>
                    <th class={`${thClass} !text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData().map((item) => {
                    const amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                    const tokenSubtext = tokenSubtextFor(item)
                    const date = formatDate(item.date)

                    return (
                      <tr
                        style={{ 'border-bottom': '1px solid rgba(255,255,255,0.06)' }}
                        class="transition-colors hover:bg-[rgba(138,43,226,0.05)]"
                      >
                        <td class={tdClass}>
                          <div class="text-white">{date.date}</div>
                          <div class="mt-0.5 font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">
                            {date.time}
                          </div>
                        </td>
                        <td class={tdClass}>{item.username || item.userId}</td>
                        <td class={tdClass}>
                          <div class="text-white">{formatTransactionType(item.type)}</div>
                          {typeSubtext(item)}
                        </td>
                        <td class={tdClass}>
                          <Show
                            when={item.characterName}
                            fallback={<span class="text-[rgba(255,255,255,0.3)]">N/A</span>}
                          >
                            <A
                              href={getModelUrl(item.categorySlug, item.characterSlug)}
                              class="hover:underline"
                            >
                              <div class="font-bebas text-[16px] tracking-[0.5px] text-white lg:text-[19px] lg:tracking-[0.6px]">
                                {item.characterName}
                              </div>
                              {item.characterRealName && (
                                <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)] lg:mt-1 lg:text-[13px]">
                                  {item.characterRealName}
                                </div>
                              )}
                            </A>
                          </Show>
                        </td>
                        <td class={tdClass}>{item.transactionId}</td>
                        <td class={tdClass}>{item.memberId}</td>
                        <td class={`${tdClass} !text-right`}>
                          <div class="font-semibold text-white">{amount}</div>
                          {tokenSubtext && (
                            <div class="mt-0.5 font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">
                              {tokenSubtext}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div class="flex flex-col gap-3 p-4 sm:hidden">
              {tableData().map((item) => {
                const amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                const tokenSubtext = tokenSubtextFor(item)
                const date = formatDate(item.date)

                return (
                  <div
                    class="grid w-full grid-cols-2 gap-x-3.5 gap-y-2.5 rounded-xl border border-[rgba(255,255,255,0.06)] p-4"
                    style={{ background: 'rgba(255,255,255,0.02)' }}
                  >
                    {/* Date + Amount — full width header */}
                    <div class="col-span-2 flex items-start justify-between border-b border-[rgba(255,255,255,0.06)] pb-2.5">
                      <div>
                        <div class="font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
                          {date.date}
                        </div>
                        <div class="mt-0.5 font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">
                          {date.time}
                        </div>
                      </div>
                      <div class="text-right">
                        <div class="font-semibold text-white">{amount}</div>
                        {tokenSubtext && (
                          <div class="mt-0.5 font-rajdhani text-[11px] text-[rgba(255,255,255,0.4)]">
                            {tokenSubtext}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* User — full width */}
                    <div class="col-span-2">
                      <div class={labelClass}>User</div>
                      <div class={valueClass}>{item.username || item.userId}</div>
                    </div>

                    {/* Type */}
                    <div>
                      <div class={labelClass}>Type</div>
                      <div class={valueClass}>{formatTransactionType(item.type)}</div>
                      {typeSubtext(item)}
                    </div>

                    {/* Character */}
                    <div>
                      <div class={labelClass}>Character</div>
                      <Show
                        when={item.characterName}
                        fallback={
                          <div class="font-rajdhani text-[13px] text-[rgba(255,255,255,0.3)]">
                            N/A
                          </div>
                        }
                      >
                        <A
                          href={getModelUrl(item.categorySlug, item.characterSlug)}
                          class="hover:underline"
                        >
                          <div class="font-bebas text-[16px] tracking-[0.5px] text-white">
                            {item.characterName}
                          </div>
                          {item.characterRealName && (
                            <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)]">
                              {item.characterRealName}
                            </div>
                          )}
                        </A>
                      </Show>
                    </div>

                    {/* Transaction ID — full width */}
                    <div class="col-span-2">
                      <div class={labelClass}>Transaction ID</div>
                      <div class={`${valueClass} break-all`}>{item.transactionId}</div>
                    </div>

                    {/* Member ID — full width */}
                    <div class="col-span-2">
                      <div class={labelClass}>Member ID</div>
                      <div class={`${valueClass} break-all`}>{item.memberId}</div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </Show>
        </div>
      </AccountLayout>
      <Footer />
    </>
  )
}
